import { create } from "zustand"
import { FunctionsHttpError } from "@supabase/supabase-js"
import { supabase } from "@/lib/supabase"
import type { Tables, TablesInsert } from "@/types/database"
import { useAuthStore } from "@/stores/authStore"
import { seedPagesForOrg } from "@/lib/seedPages"

export type Organization = Tables<"organizations">

/**
 * NOS-209: localStorage key marking the org a user just created/onboarded in
 * this browser. It survives a full page reload and — because localStorage is
 * shared same-origin — is visible when onboarding opens the builder in a new
 * tab (window.open), where in-memory Zustand state is empty. Cleared on the
 * next deliberate org switch so it never permanently overrides a favorite.
 */
const JUST_ONBOARDED_ORG_KEY = "nos:justOnboardedOrgId"

function readJustOnboardedOrgId(): string | null {
  try {
    return typeof window !== "undefined"
      ? window.localStorage.getItem(JUST_ONBOARDED_ORG_KEY)
      : null
  } catch {
    return null
  }
}

function writeJustOnboardedOrgId(orgId: string): void {
  try {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(JUST_ONBOARDED_ORG_KEY, orgId)
    }
  } catch {
    /* ignore — a missing/blocked localStorage just falls back to last_selected */
  }
}

function clearJustOnboardedOrgId(): void {
  try {
    if (typeof window !== "undefined") {
      window.localStorage.removeItem(JUST_ONBOARDED_ORG_KEY)
    }
  } catch {
    /* ignore */
  }
}

export interface OrgAiProfileInput {
  mission?: string | null
  vision?: string | null
  values?: string | null
  who_served?: string | null
  years_operating?: string | null
  impact_stats?: string | null
  tone?: string | null
}

export interface InitiateCrawlOptions {
  /** NOS-184: per-job page cap; server honors it only for platform admins */
  pageCap?: number
  /** NOS-189: false = import content only (gate source design). Default (undefined) = content + design. */
  importDesign?: boolean
}

export interface InitiateCrawlResult {
  ok: boolean
  error?: string
  /** NOS-178: id of the import_jobs row tracking the async import */
  jobId?: string
  crawlResultId?: string
}

export async function errorBodyMessageFromFunctionsHttpError(error: unknown): Promise<string | undefined> {
  if (!(error instanceof FunctionsHttpError)) return undefined
  const res = error.context
  if (!(res instanceof Response)) return undefined
  try {
    const clone = res.clone()
    const ct = clone.headers.get("Content-Type") ?? ""
    if (!ct.includes("application/json")) return undefined
    const body = (await clone.json()) as unknown
    if (body && typeof body === "object" && "error" in body) {
      const msg = (body as { error: unknown }).error
      return typeof msg === "string" ? msg : undefined
    }
  } catch {
    /* ignore */
  }
  return undefined
}

interface OrgState {
  currentOrg: Organization | null
  userOrgs: Organization[]
  /** Children sponsored by orgs the user belongs to (NOS-165) — not member orgs. */
  sponsoredOrgs: Organization[]
  favoriteOrgId: string | null
  isLoading: boolean
  fetchError: string | null
  createOrg: (data: CreateOrgInput) => Promise<{ data: Organization | null; error: Error | null }>
  createChildOrg: (
    data: CreateChildOrgInput
  ) => Promise<{ data: Organization | null; error: Error | null; seedWarning?: string }>
  saveOrgAiProfile: (orgId: string, data: OrgAiProfileInput) => Promise<{ error: Error | null }>
  /** NOS-114: Initiate site crawl via crawl-site edge function. */
  initiateCrawl: (
    url: string,
    orgId: string,
    opts?: InitiateCrawlOptions
  ) => Promise<InitiateCrawlResult>
  setCurrentOrg: (org: Organization | null) => void
  fetchUserOrgs: () => Promise<void>
  checkUserHasOrg: () => Promise<boolean>
  switchOrg: (orgId: string) => Promise<void>
  toggleFavoriteOrg: (orgId: string) => Promise<void>
  loadUserPreferences: () => Promise<void>
}

export interface CreateOrgInput {
  name: string
  slug: string
  mission_statement?: string | null
  ein?: string | null
  logo_url?: string | null
}

export interface CreateChildOrgInput {
  sponsorOrgId: string
  name: string
  slug: string
  mode: "fiscal" | "affiliated"
  adminFeePct: number
  /** True when no leader invite is sent — sponsorship starts active immediately. */
  activate: boolean
}

type MemberRow = { role: string; organizations: Organization | null }

export const useOrgStore = create<OrgState>((set, get) => ({
  currentOrg: null,
  userOrgs: [],
  sponsoredOrgs: [],
  favoriteOrgId: null,
  isLoading: false,
  fetchError: null,

  setCurrentOrg: (org) => set({ currentOrg: org }),

  fetchUserOrgs: async () => {
    const { user } = useAuthStore.getState()
    if (!user?.id) {
      set({ userOrgs: [], fetchError: null })
      return
    }
    set({ isLoading: true, fetchError: null })
    const { data: members, error } = await supabase
      .from("organization_members")
      .select("role, organizations(*)")
      .eq("user_id", user.id)
    if (error) {
      set({ userOrgs: [], isLoading: false, fetchError: error.message })
      return
    }
    const orgs =
      (members ?? [])
        .filter((m): m is MemberRow & { organizations: Organization } => m.organizations != null)
        .map((m) => m.organizations) ?? []
    // Sponsored children (NOS-165): orgs linked under any of the user's orgs
    // via sponsorships. RLS returns rows only for sponsor-org members; the
    // child org join resolves only with can_view_child_financials.
    let sponsoredOrgs: Organization[] = []
    if (orgs.length > 0) {
      const { data: links } = await supabase
        .from("sponsorships")
        .select("child:organizations!sponsorships_child_org_id_fkey(*)")
        .in(
          "sponsor_org_id",
          orgs.map((o) => o.id)
        )
        .in("status", ["invited", "active", "transferring"])
      sponsoredOrgs = (links ?? [])
        .map((l) => l.child as unknown as Organization | null)
        .filter((o): o is Organization => o != null && !orgs.some((m) => m.id === o.id))
    }
    const { data: prefs } = await supabase
      .from("user_preferences")
      .select("favorite_org_id, last_selected_org_id")
      .maybeSingle()
    const favoriteOrgId = (prefs?.favorite_org_id as string | null) ?? null
    const lastSelectedOrgId = (prefs?.last_selected_org_id as string | null) ?? null
    // NOS-209: currentOrg resolution precedence (highest → lowest):
    //   1. In-session explicit choice — the org already in memory (a dashboard
    //      switch, or a createOrg earlier this session), if still accessible.
    //   2. Just-created / just-onboarded — an org created via onboarding in this
    //      browser. Persisted in localStorage so it wins after a full reload and
    //      when onboarding opens the builder in a NEW TAB (window.open), where
    //      in-memory state is empty. This is what made a fresh onboard land on the
    //      previous org before; it must outrank the favorite. Cleared on the next
    //      explicit switch (switchOrg).
    //   3. Favorite org — the user's pinned preference.
    //   4. Last-selected org — the most recent dashboard switch (user_preferences).
    //   5. First membership — deterministic fallback.
    const existingCurrentOrg = get().currentOrg
    const justOnboardedOrgId = readJustOnboardedOrgId()
    const currentOrg =
      orgs.find((o) => o.id === existingCurrentOrg?.id) ??
      sponsoredOrgs.find((o) => o.id === existingCurrentOrg?.id) ??
      orgs.find((o) => o.id === justOnboardedOrgId) ??
      orgs.find((o) => o.id === favoriteOrgId) ??
      orgs.find((o) => o.id === lastSelectedOrgId) ??
      orgs[0] ??
      null
    set({
      userOrgs: orgs,
      sponsoredOrgs,
      currentOrg,
      favoriteOrgId,
      isLoading: false,
      fetchError: null,
    })
  },

  loadUserPreferences: async () => {
    const { user } = useAuthStore.getState()
    if (!user?.id) return
    const { data } = await supabase
      .from("user_preferences")
      .select("favorite_org_id, last_selected_org_id")
      .maybeSingle()
    if (data) {
      set({ favoriteOrgId: data.favorite_org_id })
    }
  },

  switchOrg: async (orgId: string) => {
    console.log("[orgStore] switchOrg called with:", orgId)

    const { userOrgs, sponsoredOrgs } = get()
    const targetOrg =
      userOrgs?.find((o) => o.id === orgId) ?? sponsoredOrgs?.find((o) => o.id === orgId)

    if (!targetOrg) {
      console.error("[orgStore] Org not found:", orgId)
      return
    }

    console.log("[orgStore] Switching to org:", targetOrg.name)

    // NOS-209: a deliberate switch is the user's new explicit choice — retire the
    // just-onboarded marker so it stops overriding the favorite in future sessions.
    clearJustOnboardedOrgId()

    // Update current org in state
    set({ currentOrg: targetOrg })

    // Save last selected (but NOT as favorite); preserve existing favorite_org_id
    const { favoriteOrgId } = get()
    const { user } = useAuthStore.getState()
    if (user) {
      console.log("[orgStore] Saving last_selected_org_id:", orgId)

      const { data, error } = await supabase
        .from("user_preferences")
        .upsert(
          {
            user_id: user.id,
            last_selected_org_id: orgId,
            ...(favoriteOrgId != null && { favorite_org_id: favoriteOrgId }),
          },
          { onConflict: "user_id" }
        )
        .select()
        .single()

      console.log("[orgStore] Upsert result:", { data, error })

      if (error) {
        console.error("[orgStore] Failed to save preference:", error)
      }
    }

    // DO NOT RELOAD - let React re-render with new org
    console.log("[orgStore] Org switched (no reload)")
  },

  toggleFavoriteOrg: async (orgId: string) => {
    console.log("[orgStore] toggleFavoriteOrg called with:", orgId)

    const { favoriteOrgId } = get()
    const newFavoriteId = favoriteOrgId === orgId ? null : orgId

    console.log("[orgStore] New favorite:", newFavoriteId)
    set({ favoriteOrgId: newFavoriteId })

    const { user } = useAuthStore.getState()
    if (user) {
      console.log("[orgStore] Saving favorite_org_id:", newFavoriteId)

      const { data: prefs } = await supabase
        .from("user_preferences")
        .select("last_selected_org_id")
        .maybeSingle()

      const { data, error } = await supabase
        .from("user_preferences")
        .upsert(
          {
            user_id: user.id,
            favorite_org_id: newFavoriteId,
            last_selected_org_id: prefs?.last_selected_org_id ?? undefined,
          },
          { onConflict: "user_id" }
        )
        .select()
        .single()

      console.log("[orgStore] Upsert result:", { data, error })

      if (error) {
        console.error("[orgStore] Failed to save favorite:", error)
      }
    }
  },

  checkUserHasOrg: async () => {
    await get().fetchUserOrgs()
    const { userOrgs } = get()
    return userOrgs.length > 0
  },

  createOrg: async (input) => {
    const user = useAuthStore.getState().user
    if (!user?.id) {
      return { data: null, error: new Error("Not authenticated") }
    }

    set({ isLoading: true })

    const orgInsert: TablesInsert<"organizations"> = {
      name: input.name.trim(),
      slug: input.slug.trim().toLowerCase(),
      mission_statement: input.mission_statement?.trim() || null,
      ein: input.ein?.trim() || null,
      logo_url: input.logo_url ?? null,
      is_verified: false,
      stripe_onboarding_complete: false,
    }

    const { data: newOrg, error: orgError } = await supabase
      .from("organizations")
      .insert(orgInsert)
      .select()
      .single()

    if (orgError) {
      set({ isLoading: false })
      return { data: null, error: orgError as unknown as Error }
    }

    if (!newOrg) {
      set({ isLoading: false })
      return { data: null, error: new Error("Failed to create organization") }
    }

    const { error: memberError } = await supabase.from("organization_members").insert({
      org_id: newOrg.id,
      user_id: user.id,
      role: "owner",
    })

    if (memberError) {
      set({ isLoading: false })
      return { data: null, error: memberError as unknown as Error }
    }

    const seedError = await seedPagesForOrg(newOrg.id)
    if (seedError) {
      set({ isLoading: false })
      return { data: null, error: seedError }
    }

    // NOS-209: a freshly created org is a deliberate current-org choice. Persist
    // it so it survives navigation into the builder, a full page reload, and the
    // onboarding "Preview in builder" new-tab (window.open) — all of which start
    // with empty in-memory state and previously resolved to the prior org.
    // - last_selected_org_id: durable fallback (preserve any existing favorite).
    // - just-onboarded marker: outranks the favorite until the next explicit switch.
    const { favoriteOrgId } = get()
    const { error: prefError } = await supabase.from("user_preferences").upsert(
      {
        user_id: user.id,
        last_selected_org_id: newOrg.id,
        ...(favoriteOrgId != null && { favorite_org_id: favoriteOrgId }),
      },
      { onConflict: "user_id" }
    )
    if (prefError) {
      // Non-fatal: the in-memory currentOrg still lands the org for this tab.
      console.error("[orgStore] Failed to persist last_selected after createOrg:", prefError)
    }
    writeJustOnboardedOrgId(newOrg.id)

    set({ currentOrg: newOrg, userOrgs: [...get().userOrgs, newOrg], isLoading: false })
    return { data: newOrg, error: null }
  },

  createChildOrg: async (input) => {
    const user = useAuthStore.getState().user
    if (!user?.id) {
      return { data: null, error: new Error("Not authenticated") }
    }

    // Org + sponsorship are created atomically by the RPC — the sponsor is
    // never a member of the child (access rides is_sponsor_of), so client-side
    // sequencing could not clean up a partial failure.
    const { data: newOrg, error: rpcError } = await supabase.rpc("create_sponsored_child_org", {
      p_sponsor_org_id: input.sponsorOrgId,
      p_name: input.name.trim(),
      p_slug: input.slug.trim().toLowerCase(),
      p_mode: input.mode,
      p_admin_fee_pct: input.adminFeePct,
      p_activate: input.activate,
    })

    if (rpcError) {
      const friendly = /organizations_slug_key|duplicate key/.test(rpcError.message)
        ? "That subdomain is already taken"
        : rpcError.message
      return { data: null, error: new Error(friendly) }
    }
    if (!newOrg) {
      return { data: null, error: new Error("Failed to create organization") }
    }

    const org = newOrg as unknown as Organization

    // Seeding failure is recoverable (the leader can pick a theme later), so
    // it surfaces as a warning rather than failing the creation.
    const seedError = await seedPagesForOrg(org.id)

    set({ sponsoredOrgs: [...get().sponsoredOrgs, org] })
    return {
      data: org,
      error: null,
      ...(seedError ? { seedWarning: seedError.message } : {}),
    }
  },

  saveOrgAiProfile: async (orgId, data) => {
    const { error } = await supabase.from("org_ai_profiles").upsert(
      {
        org_id: orgId,
        mission: data.mission?.trim() || null,
        vision: data.vision?.trim() || null,
        values: data.values?.trim() || null,
        who_served: data.who_served?.trim() || null,
        years_operating: data.years_operating?.trim() || null,
        impact_stats: data.impact_stats?.trim() || null,
        tone: data.tone?.trim() || null,
        updated_at: new Date().toISOString(),
      },
      { onConflict: "org_id" }
    )
    return { error: error as unknown as Error | null }
  },

  initiateCrawl: async (url, orgId, opts) => {
    if (!url?.trim() || !orgId) {
      return { ok: false, error: "URL and org ID are required" }
    }
    const trimmed = url.trim()
    if (!trimmed.startsWith("http://") && !trimmed.startsWith("https://")) {
      return { ok: false, error: "URL must start with http:// or https://" }
    }
    const { data: sessWrap } = await supabase.auth.getSession()
    const s = sessWrap.session

    const invokeCrawl = (accessToken: string | undefined) =>
      supabase.functions.invoke("crawl-site", {
        body: {
          url: trimmed,
          org_id: orgId,
          // NOS-184: honored server-side only for platform admins
          ...(opts?.pageCap != null ? { page_cap: opts.pageCap } : {}),
          // NOS-189: only send when opting out — server defaults to true
          ...(opts?.importDesign === false ? { import_design: false } : {}),
        },
        ...(accessToken
          ? { headers: { Authorization: `Bearer ${accessToken}` } }
          : {}),
      })

    let { data, error } = await invokeCrawl(s?.access_token)
    const statusFirst = (error as { context?: { status?: number } } | null)?.context?.status

    if (error && statusFirst === 401) {
      await supabase.auth.refreshSession()
      const { data: sess2 } = await supabase.auth.getSession()
      ;({ data, error } = await invokeCrawl(sess2.session?.access_token))
    }

    if (error) {
      const serverMsg = await errorBodyMessageFromFunctionsHttpError(error)
      return {
        ok: false,
        error:
          serverMsg ?? error.message ?? "Could not crawl this URL. Please try again.",
      }
    }
    const errMsg = (data as { error?: string })?.error
    if (errMsg) {
      return { ok: false, error: errMsg }
    }
    const result = data as { job_id?: string; crawl_result_id?: string }
    if (!result?.job_id) {
      return { ok: false, error: "Import did not start. Please try again." }
    }
    return {
      ok: true,
      jobId: result.job_id,
      crawlResultId: result?.crawl_result_id,
    }
  },
}))
